import { Button } from "@/components/ui/button";
import { useModalStore } from "@/stores/use-modal-store";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { ArrowDownTrayIcon } from "@heroicons/react/24/solid";
import ErrorImg from "@/assets/fallbacks/error-img.png";

/* Prop Types */
export interface ExportStatusModalProps {
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  /** Direct download URL — required when isSuccess is true */
  fileUrl?: string;
  /** Suggested filename for the download. Needed for blob/object URLs where the name can't be inferred. */
  downloadFilename?: string;
  /** Title shown during the loading state */
  loadingTitle: string;
  /** Body text shown during the loading state */
  loadingDescription: string;
  /** Title shown when the export succeeded */
  successTitle: string;
  /** Body text shown when the export failed */
  errorDescription: string;
}

/*
 * ExportStatusModal
 * Generic export-progress modal covering three states: loading → success → error.
 * Keeps all API concerns out — callers derive the state booleans and pass them in.
 */
export const ExportStatusModal = ({
  isLoading,
  isSuccess,
  isError,
  fileUrl,
  downloadFilename,
  loadingTitle,
  loadingDescription,
  successTitle,
  errorDescription,
}: ExportStatusModalProps) => {
  /* Hooks */
  const { close } = useModalStore((state) => state.modalActions);

  /* Render */
  return (
    <>
      {/* Close button */}
      <div className="flex items-center justify-end">
        <XMarkIcon onClick={close} className="size-5 cursor-pointer" />
      </div>

      <div className="flex flex-col items-center justify-center p-6 gap-4">
        {isLoading ? (
          <>
            <video src="/videos/upload-animation.webm" className="w-36" autoPlay muted loop />
            <h2>{loadingTitle}</h2>
            <p>{loadingDescription}</p>
          </>
        ) : isSuccess ? (
          <>
            <div className="bg-success rounded-2xl p-6 m-6 text-black">
              <ArrowDownTrayIcon className="size-9" />
            </div>
            <h2>{successTitle}</h2>
            <a href={fileUrl} download={downloadFilename}>
              <Button variant="primary">Download</Button>
            </a>
          </>
        ) : isError ? (
          <>
            <img src={ErrorImg} alt="error" className="w-32" />
            <h2>Something went wrong...</h2>
            <p>{errorDescription}</p>
            <Button variant="primary" onClick={close}>
              Close
            </Button>
          </>
        ) : null}
      </div>
    </>
  );
};
